import type { FC, ReactNode } from 'react'

import { cn } from '@/lib/utils'
import { useMounted } from '@/hooks/use-mounted'

type DateTimeVariant = 'datetime' | 'date' | 'relative'

interface DateTimeProps {
    value: string
    variant?: DateTimeVariant
    className?: string
}

const LOCALE = 'es-ES'

const PLACEHOLDERS: Record<DateTimeVariant, string> = {
    datetime: '00 sep 0000, 00:00',
    date: '00 sep 0000',
    relative: 'hace 00 min'
}

let dateTimeFormat: Intl.DateTimeFormat | null = null
let dateFormat: Intl.DateTimeFormat | null = null
let relativeFormat: Intl.RelativeTimeFormat | null = null

const formatDateTime = (date: Date): string => {
    dateTimeFormat ??= new Intl.DateTimeFormat(LOCALE, {
        day: 'numeric',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
    })

    return dateTimeFormat.format(date)
}

const formatDate = (date: Date): string => {
    dateFormat ??= new Intl.DateTimeFormat(LOCALE, { day: 'numeric', month: 'short', year: 'numeric' })

    return dateFormat.format(date)
}

const formatRelative = (date: Date): string => {
    relativeFormat ??= new Intl.RelativeTimeFormat(LOCALE, { numeric: 'auto' })

    const seconds = Math.round((date.getTime() - Date.now()) / 1000)
    const absolute = Math.abs(seconds)

    if (absolute < 45) return 'hace un momento'
    if (absolute < 3600) return relativeFormat.format(Math.round(seconds / 60), 'minute')
    if (absolute < 86400) return relativeFormat.format(Math.round(seconds / 3600), 'hour')
    if (absolute < 86400 * 7) return relativeFormat.format(Math.round(seconds / 86400), 'day')

    return formatDate(date)
}

const DateTime: FC<DateTimeProps> = ({ value, variant = 'datetime', className }): ReactNode => {
    const mounted = useMounted()
    const date = new Date(value)

    if (!mounted || Number.isNaN(date.getTime())) {
        return (
            <time dateTime={value} className={cn('invisible', className)} aria-hidden='true'>
                {PLACEHOLDERS[variant]}
            </time>
        )
    }

    const text = variant === 'relative' ? formatRelative(date) : variant === 'date' ? formatDate(date) : formatDateTime(date)

    return (
        <time dateTime={value} title={formatDateTime(date)} className={className}>
            {text}
        </time>
    )
}

export default DateTime
